import { lazy, Suspense, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useUsage, useUsageEvents, type UsageBucket, type UsageSummary } from "@/api/queries";
import { useFormatter } from "@/artifacts/format";
import type { Artifact } from "@/artifacts/types";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { EmptyState } from "@/components/ui/EmptyState";
import { Spinner } from "@/components/ui/Spinner";
import { AlertIcon, ChartIcon, ClockIcon, CoinIcon, SettingsIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * Tokens, latency and estimated cost (PRD FR-3.5, NFR-7).
 *
 * Every figure here carries the thing that makes it usable. A cost is an
 * *estimate* from an editable price table, never a bill; a model nobody has
 * priced is counted as unpriced rather than as free; a provider that reported
 * no tokens is counted as silent rather than as having used none. The page
 * says all three out loud, because the alternative is a confident total that
 * quietly folded in every answer it could not measure.
 *
 * The by-day chart is the M3 renderer fed a hand-built payload rather than a
 * second charting path — same theme, same formatter, same axes.
 */

// Lazily, exactly as `ArtifactCard` does. A static import here would pull
// ECharts back into the entry chunk and make the unlock page pay for a library
// that only two surfaces use (PRD NFR-1) — Vite says so out loud when the same
// module is imported both ways.
const ChartCanvas = lazy(() =>
  import("@/components/artifacts/ChartCanvas").then((module) => ({
    default: module.ChartCanvas,
  })),
);

const RANGES = [7, 30, 90] as const;

/** Money in the currency the *price* was quoted in, not the deployment's. */
function money(value: number | null | undefined, currency: string | null | undefined): string {
  if (value == null || !currency) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: value < 1 ? 4 : 2,
    }).format(value);
  } catch {
    return `${value.toFixed(4)} ${currency}`;
  }
}

function Metric({
  label,
  value,
  hint,
  icon,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  icon?: React.ReactNode;
  tone?: "warning";
}) {
  return (
    <div className="border-border rounded-container bg-card flex flex-col gap-1 border p-4">
      <span className="type-caption text-ink-disabled inline-flex items-center gap-1.5">
        {icon}
        {label}
      </span>
      <span className={cn("type-metric text-ink", tone === "warning" && "text-warning")}>
        {value}
      </span>
      {hint ? <span className="type-support text-ink-secondary">{hint}</span> : null}
    </div>
  );
}

function dailyChart(buckets: UsageBucket[]): Artifact | null {
  if (buckets.length < 2) return null;
  return {
    type: "bar_chart",
    title: "Answers per day",
    index: 0,
    x: "day",
    y: "tokens",
    series_by: "",
    size: "",
    text: "",
    items: [],
    notes: [],
    swap_options: [],
    family: "categorical",
    from_query: null,
    degraded_from: null,
    data: null,
    chart: {
      categories: buckets.map((bucket) => bucket.key),
      series: [
        {
          name: "Tokens",
          values: buckets.map((bucket) => bucket.total_tokens),
        },
      ],
      points: [],
      cells: [],
      y_categories: [],
      x_label: "",
      y_label: "Tokens",
      value_format: "integer",
      category_format: "text",
      x_format: "text",
      size_format: "number",
      size_label: "",
    },
  } as unknown as Artifact;
}

function BucketTable({
  rows,
  keyLabel,
  emptyLabel,
}: {
  rows: UsageBucket[];
  keyLabel: string;
  emptyLabel: string;
}) {
  const formatter = useFormatter();
  if (!rows.length) {
    return <p className="type-support text-ink-secondary px-1 py-3">{emptyLabel}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="text-ink-secondary type-caption">
            <th className="px-3 py-2 text-start font-medium">{keyLabel}</th>
            <th className="px-3 py-2 text-end font-medium">Answers</th>
            <th className="px-3 py-2 text-end font-medium">Tokens in / out</th>
            <th className="px-3 py-2 text-end font-medium">Avg latency</th>
            <th className="px-3 py-2 text-end font-medium">Est. cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-border border-t">
              <td className="px-3 py-2">
                <span className="type-support text-ink line-clamp-2">{row.label}</span>
                {row.detail ? (
                  <span className="type-caption text-ink-disabled block">{row.detail}</span>
                ) : null}
              </td>
              <td className="type-support text-ink px-3 py-2 text-end tabular-nums">
                {row.answers}
              </td>
              <td className="type-support text-ink px-3 py-2 text-end tabular-nums">
                {formatter.axis(row.input_tokens, "integer")} /{" "}
                {formatter.axis(row.output_tokens, "integer")}
              </td>
              <td className="type-support text-ink-secondary px-3 py-2 text-end tabular-nums">
                {row.avg_latency_ms == null ? "—" : `${(row.avg_latency_ms / 1000).toFixed(1)}s`}
              </td>
              <td className="px-3 py-2 text-end">
                <span className="type-support text-ink tabular-nums">
                  {money(row.cost_total, row.currency)}
                </span>
                {row.unpriced ? (
                  <span className="type-caption text-ink-disabled block">
                    {row.unpriced} unpriced
                  </span>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Totals({ summary }: { summary: UsageSummary }) {
  const formatter = useFormatter();
  const { totals } = summary;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Metric
        label="Answers"
        value={String(totals.answers)}
        hint={`${totals.tool_calls} tool calls`}
        icon={<ChartIcon className="size-3" />}
      />
      <Metric
        label="Tokens"
        value={formatter.axis(totals.total_tokens, "integer")}
        hint={`${formatter.axis(totals.input_tokens, "integer")} in · ${formatter.axis(
          totals.output_tokens,
          "integer",
        )} out`}
      />
      <Metric
        label="Average latency"
        value={totals.avg_latency_ms == null ? "—" : `${(totals.avg_latency_ms / 1000).toFixed(1)}s`}
        icon={<ClockIcon className="size-3" />}
      />
      <Metric
        label="Estimated cost"
        value={
          totals.mixed_currencies ? "mixed currencies" : money(totals.cost_total, totals.currency)
        }
        hint={
          totals.unpriced
            ? `${totals.unpriced} answer${totals.unpriced === 1 ? "" : "s"} on unpriced models`
            : "from the price table in the catalog"
        }
        icon={<CoinIcon className="size-3" />}
        tone={totals.mixed_currencies ? "warning" : undefined}
      />
    </div>
  );
}

export function UsagePage() {
  const [days, setDays] = useState<number>(30);
  const { data: summary, isLoading } = useUsage(days);
  const { data: events } = useUsageEvents(25);
  const chart = useMemo(() => dailyChart(summary?.by_day ?? []), [summary?.by_day]);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-8">
      <header className="mb-6">
        <p className="type-caption text-ink-disabled">Admin</p>
        <h1 className="type-h1 text-ink mt-2">Usage &amp; cost</h1>
        <p className="type-body text-ink-secondary mt-2 max-w-2xl">
          One row per answer: which model produced it, how long it took, how many tokens it used
          and what that is estimated to have cost. The cost is an estimate from the price table in
          the{" "}
          <Link to="/settings/providers" className="text-accent hover:underline">
            model catalog
          </Link>
          , never a bill.
        </p>
        <div className="mt-4 flex items-center gap-1" role="tablist" aria-label="Time range">
          {RANGES.map((range) => (
            <button
              key={range}
              type="button"
              role="tab"
              aria-selected={days === range}
              onClick={() => setDays(range)}
              className={cn(
                "type-support rounded-element motion-fast px-3 py-1.5",
                days === range ? "bg-selected text-ink" : "text-ink-secondary hover:bg-hover",
              )}
            >
              {range} days
            </button>
          ))}
        </div>
      </header>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : !summary || summary.totals.answers === 0 ? (
        <EmptyState
          icon={<CoinIcon className="size-6" />}
          title="No answers in this window"
          detail="Usage is recorded per answer. Ask a question and the tokens, latency and estimated cost land here."
        />
      ) : (
        <div className="flex flex-col gap-6">
          <Totals summary={summary} />

          {summary.totals.unreported ? (
            <p className="bg-warning-subtle text-warning type-support rounded-element flex items-start gap-2 px-3 py-2">
              <AlertIcon className="mt-0.5 size-3.5 shrink-0" />
              <span>
                {summary.totals.unreported} of these answers reported no token counts at all —
                usually a run that failed before the model replied, or a provider that does not
                publish usage. Their tokens are missing from these totals rather than counted as
                zero.
              </span>
            </p>
          ) : null}

          {chart ? (
            <Card>
              <CardHeader title="Tokens per day" />
              <CardBody>
                <Suspense fallback={<div className="h-[220px]" />}>
                  <ChartCanvas artifact={chart} height={220} />
                </Suspense>
              </CardBody>
            </Card>
          ) : null}

          <Card>
            <CardHeader
              title="By model"
              action={
                <Link
                  to="/settings/providers"
                  className="type-support text-ink-secondary hover:text-ink inline-flex items-center gap-1"
                >
                  <SettingsIcon className="size-3.5" />
                  Prices
                </Link>
              }
            />
            <CardBody className="px-0">
              <BucketTable
                rows={summary.by_model}
                keyLabel="Model"
                emptyLabel="No answers yet."
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="By origin"
              description="Where the spend came from: people's questions, Cause Lens, health checks, evals."
            />
            <CardBody className="px-0">
              <BucketTable
                rows={summary.by_origin ?? []}
                keyLabel="Origin"
                emptyLabel="No answers yet."
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Top questions" />
            <CardBody className="px-0">
              <BucketTable
                rows={summary.top_questions}
                keyLabel="Question"
                emptyLabel="No questions recorded yet."
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Recent answers"
              description="The raw log — one row per answer, newest first."
            />
            <CardBody className="px-0">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="text-ink-secondary type-caption">
                      <th className="px-3 py-2 text-start font-medium">When</th>
                      <th className="px-3 py-2 text-start font-medium">Question</th>
                      <th className="px-3 py-2 text-start font-medium">Model</th>
                      <th className="px-3 py-2 text-end font-medium">Tokens</th>
                      <th className="px-3 py-2 text-end font-medium">Latency</th>
                      <th className="px-3 py-2 text-end font-medium">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(events?.entries ?? []).map((row) => (
                      <tr key={row.id} className="border-border border-t">
                        <td className="type-caption text-ink-disabled px-3 py-2 whitespace-nowrap">
                          {new Date(row.occurred_at).toLocaleString()}
                        </td>
                        <td className="type-support text-ink px-3 py-2">
                          <span className="line-clamp-1">{row.question || "—"}</span>
                        </td>
                        <td className="type-support text-ink-secondary px-3 py-2">
                          <span className="line-clamp-1">{row.model_label}</span>
                        </td>
                        <td className="type-support text-ink px-3 py-2 text-end tabular-nums">
                          {row.usage_source === "none" ? (
                            <Chip tone="neutral">not reported</Chip>
                          ) : (
                            `${row.input_tokens} / ${row.output_tokens}`
                          )}
                        </td>
                        <td className="type-support text-ink-secondary px-3 py-2 text-end tabular-nums">
                          {row.latency_ms == null ? "—" : `${(row.latency_ms / 1000).toFixed(1)}s`}
                        </td>
                        <td className="type-support text-ink px-3 py-2 text-end tabular-nums">
                          {row.cost_basis === "unpriced" ? (
                            <span className="text-ink-disabled">unpriced</span>
                          ) : (
                            money(row.cost_total, row.currency)
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}
